import { useState, useCallback } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';

interface BlogPostReactionsProps {
  postId: string;
  initialLikes: number;
  initialDislikes: number;
}

export function BlogPostReactions({ postId, initialLikes, initialDislikes }: BlogPostReactionsProps) {
  const storageKey = `blog-reaction-${postId}`;
  const existing = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;

  const [likes, setLikes] = useState(initialLikes);
  const [dislikes, setDislikes] = useState(initialDislikes);
  const [voted, setVoted] = useState<'like' | 'dislike' | null>(existing as any);
  const [animating, setAnimating] = useState<'like' | 'dislike' | null>(null);

  const updateDB = useCallback(async (newLikes: number, newDislikes: number) => {
    await supabase.from('blog_posts').update({ likes: newLikes, dislikes: newDislikes }).eq('id', postId);
  }, [postId]);

  const handleVote = useCallback(async (type: 'like' | 'dislike') => {
    setAnimating(type);
    setTimeout(() => setAnimating(null), 500);

    let newLikes = likes;
    let newDislikes = dislikes;

    if (voted === type) {
      // Cancel current vote
      if (type === 'like') newLikes--;
      else newDislikes--;
      setVoted(null);
      localStorage.removeItem(storageKey);
    } else if (voted) {
      // Switch vote
      if (voted === 'like') { newLikes--; newDislikes++; }
      else { newDislikes--; newLikes++; }
      setVoted(type);
      localStorage.setItem(storageKey, type);
    } else {
      // New vote
      if (type === 'like') newLikes++;
      else newDislikes++;
      setVoted(type);
      localStorage.setItem(storageKey, type);
    }

    setLikes(newLikes);
    setDislikes(newDislikes);
    await updateDB(newLikes, newDislikes);
  }, [voted, likes, dislikes, storageKey, updateDB]);

  return (
    <div className="flex flex-col items-center gap-3 py-8 mt-10 border-t border-border">
      <p className="text-sm font-medium text-muted-foreground">מה דעתך על הכתבה?</p>
      <div className="flex items-center gap-8">
        {/* Dislike */}
        <button onClick={() => handleVote('dislike')} className="flex flex-col items-center gap-2 group">
          <motion.div
            animate={animating === 'dislike' ? { scale: [1, 1.3, 0.9, 1.1, 1], rotate: [0, -15, 10, -5, 0] } : {}}
            transition={{ duration: 0.5 }}
            className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
              voted === 'dislike'
                ? 'border-destructive bg-destructive/10'
                : 'border-border hover:border-destructive/50'
            }`}
          >
            <ThumbsDown className={`h-6 w-6 transition-colors ${
              voted === 'dislike' ? 'text-destructive' : 'text-muted-foreground group-hover:text-destructive/70'
            }`} />
          </motion.div>
          <AnimatePresence mode="popLayout">
            <motion.span
              key={dislikes}
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 8, opacity: 0 }}
              className={`text-sm font-semibold ${voted === 'dislike' ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {dislikes}
            </motion.span>
          </AnimatePresence>
        </button>

        {/* Like */}
        <button onClick={() => handleVote('like')} className="flex flex-col items-center gap-2 group">
          <motion.div
            animate={animating === 'like' ? { scale: [1, 1.3, 0.9, 1.1, 1], rotate: [0, 15, -10, 5, 0] } : {}}
            transition={{ duration: 0.5 }}
            className={`w-14 h-14 rounded-full border-2 flex items-center justify-center transition-colors cursor-pointer ${
              voted === 'like'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <ThumbsUp className={`h-6 w-6 transition-colors ${
              voted === 'like' ? 'text-primary' : 'text-muted-foreground group-hover:text-primary/70'
            }`} />
          </motion.div>
          <AnimatePresence mode="popLayout">
            <motion.span
              key={likes}
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 8, opacity: 0 }}
              className={`text-sm font-semibold ${voted === 'like' ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {likes}
            </motion.span>
          </AnimatePresence>
        </button>
      </div>
    </div>
  );
}
